import { Dialog } from '@headlessui/react'
import { HubSpotFormIDs } from '@vangst/services/hubspot'
import { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  MdCheckCircle,
  MdOutlineCancel,
  MdOutlineThumbDown,
  MdOutlineThumbUp,
  MdThumbDown,
  MdThumbUp,
} from 'react-icons/md'
import AlertText from '../../../components/feedback/AlertText'
import { DialogModal } from '../../../components/feedback/DialogModal'
import Checkbox from '../../../components/forms/Checkbox'
import ControlGroup from '../../../components/forms/ControlGroup'
import Input from '../../../components/forms/Input'
import Radio from '../../../components/forms/Radio'
import TextareaCounter from '../../../components/forms/TextareaCounter'
import ClickyBtn from '../../../components/navigation/ClickyBtn'
import ClickyLink from '../../../components/navigation/ClickyLink'
import RadioOverlay from '../../application/forms/RadioOverlay'
import useHubSpotForm from '../../application/useHubSpotForm'
import validations from '../../application/validations'

type PropsType = {
  readonly email: string
  readonly firstName: string
  readonly handleCancel: () => void
  readonly trainingName: string
}

function DialogCourseFeedback({
  email,
  firstName,
  handleCancel,
  trainingName,
}: PropsType) {
  const [success, setSuccess] = useState(false)
  const focusRef = useRef<HTMLButtonElement>(null)

  // NOTE: These must match the values in the hubspot form
  // https://share.hsforms.com/1ShnE_5uFRAGVAWxRhaj3OQ3eexj
  const defaultValues = {
    company: '',
    course_value: '',
    course_communication_preferences: null,
    course_feedback: '',
    did_you_learn_something_taking_this_course_: null,
    jobtitle: '',
  }

  type FormInput = typeof defaultValues

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<FormInput>({ defaultValues })

  const { ref: courseValueRef, ...courseValueProps } = register(
    'course_value',
    validations.generic,
  )

  const { ref: learnedStuffRef, ...learnedStuffProps } = register(
    'did_you_learn_something_taking_this_course_',
    validations.generic,
  )

  const { course_value: courseValueState } = watch()

  const { ref: communicationPreferencesRef, ...communicationPreferencesProps } =
    register('course_communication_preferences')

  const {
    handleSubmitHSForm,
    saving,
    error: hsError,
  } = useHubSpotForm(HubSpotFormIDs.CourseFeedback)

  const submit = (fd: FormInput) => {
    const input = {
      submittedAt: Date.now(),
      fields: [
        { name: 'company', value: fd.company },
        { name: 'course_value', value: fd.course_value },
        {
          name: 'course_communication_preferences',
          value:
            fd?.course_communication_preferences != null
              ? (fd.course_communication_preferences as string[]).join(';')
              : '',
        },
        {
          name: 'course_feedback',
          value: fd.course_feedback,
        },
        {
          name: 'did_you_learn_something_taking_this_course_',
          value: fd.did_you_learn_something_taking_this_course_,
        },
        { name: 'email', value: email },
        { name: 'firstName', value: firstName },
        { name: 'jobtitle', value: fd.jobtitle },
        { name: 'trainingName', value: trainingName },
      ],
      context: {
        pageName: trainingName,
      },
    }

    handleSubmitHSForm(input)

    if (Object.keys(errors).length === 0 && hsError === false) {
      setSuccess(true)
      reset(defaultValues)
    }
  }

  return (
    <>
      <DialogModal onClose={handleCancel} open id="dialog-feedback">
        <DialogModal.Panel className="relative">
          <ClickyLink
            id="close-feedback-modal"
            as="icon"
            icon={MdOutlineCancel}
            className="link-grey absolute right-1 top-1 z-10"
            onClick={handleCancel}
          >
            Close
          </ClickyLink>
          <Dialog.Title className="mt-4 text-center heading-sm">
            What did you think of the course?
          </Dialog.Title>
          <Dialog.Description className="mb-4 text-center text-sm">
            Your feedback is important to us! If you have a minute to answer a
            few questions, it would help us create more valuable content in the
            future.
          </Dialog.Description>
          <form
            className="flex w-full flex-col items-center gap-y-8"
            onSubmit={handleSubmit(submit)}
            onReset={handleCancel}
          >
            <ControlGroup
              classNameList="!flex-row"
              error={errors?.course_value?.message}
              label="Choose course enjoyment"
              hideLabel
              layout="row"
              name="course_value"
              required
              type="radio"
            >
              <RadioOverlay
                classNameLabel="flex-col max-w-30 text-sm text-center"
                id="course_value_true"
                label="I Liked The Course"
                ref={courseValueRef}
                value="true"
                {...courseValueProps}
              >
                <span className="m-auto flex h-14 w-18 items-center justify-center rounded-md bg-grey-lightest p-2">
                  {courseValueState === 'true' ? (
                    <MdThumbUp />
                  ) : (
                    <MdOutlineThumbUp />
                  )}
                </span>
              </RadioOverlay>
              <RadioOverlay
                classNameLabel="flex-col max-w-30 text-sm text-center"
                id="course_value_false"
                label="Could Use Some Improvement"
                ref={courseValueRef}
                value="false"
                {...courseValueProps}
              >
                <span className="m-auto flex h-14 w-18 items-center justify-center rounded-md bg-grey-lightest p-2">
                  {courseValueState === 'false' ? (
                    <MdThumbDown />
                  ) : (
                    <MdOutlineThumbDown />
                  )}
                </span>
              </RadioOverlay>
            </ControlGroup>
            <TextareaCounter
              error={errors?.course_feedback?.message}
              hideLabel
              label="What would be an interesting course we should add?"
              len={0}
              max={3600}
              placeholder="What would be an interesting course we should add?"
              {...register('course_feedback', validations.maxLength1200)}
              rows={4}
            />
            <hr className=" h-1 w-full text-grey-mid" />
            <div className="grid-two-up w-full gap-4">
              <ControlGroup
                error={
                  errors?.did_you_learn_something_taking_this_course_?.message
                }
                label="Did you learn anything from this course?"
                layout="col"
                name="learned_stuff"
                type="radio"
              >
                <Radio
                  ref={learnedStuffRef}
                  label="Yes"
                  value="Yes"
                  {...learnedStuffProps}
                />
                <Radio
                  ref={learnedStuffRef}
                  label="No"
                  value="No"
                  {...learnedStuffProps}
                />
              </ControlGroup>
              <ControlGroup
                error={errors?.course_communication_preferences?.message}
                label="Would you be interested in receiving:"
                layout="col"
                name="course_communication_preferences"
                type="checkbox"
              >
                <Checkbox
                  id="communication_preferences_new_courses"
                  label="Updates about new courses"
                  ref={communicationPreferencesRef}
                  value="new_courses"
                  {...communicationPreferencesProps}
                />
                <Checkbox
                  id="communication_preferences_related_jobs"
                  label="Information about jobs related to this course"
                  ref={communicationPreferencesRef}
                  value="related_jobs"
                  {...communicationPreferencesProps}
                />
              </ControlGroup>
            </div>
            <div className="flex w-full gap-2">
              <Input
                id="jobtitle"
                error={errors?.jobtitle?.message}
                label="Job Title (Optional)"
                placeholder="Enter Job Title"
                {...register('jobtitle')}
              />

              <Input
                id="company"
                error={errors?.jobtitle?.message}
                label="Company (Optional)"
                placeholder="Enter Company Name"
                {...register('company')}
              />
            </div>
            <div className="flow-y-sm sm:flow-x-sm">
              <ClickyBtn
                className="mood-orange w-64"
                disabled={saving}
                type="submit"
                ref={focusRef}
              >
                Submit Feedback
              </ClickyBtn>
              <ClickyBtn
                className="mood-grey-light w-64"
                disabled={saving}
                type="reset"
              >
                Cancel
              </ClickyBtn>
            </div>
            {success && (
              <AlertText className="text-green" icon={MdCheckCircle}>
                Form Submitted!
              </AlertText>
            )}
          </form>
        </DialogModal.Panel>
      </DialogModal>
    </>
  )
}

export default DialogCourseFeedback
